import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { ZodError } from 'zod';
import { SnackBarService } from '../../services/snack-bar.service';
import { XlsxService } from '../../services/xlsx.service';
import { MessageType } from '../../types/snack-bar.types';
import { FileTabConfig } from '../upload-view-component/upload-view-component.type';
import { UploadedFileDetail } from '../upload-file/upload-file.type';

@Component({
    selector: 'app-upload-file-multi-tab',
    templateUrl: './upload-file-multi-tab.component.html',
    styleUrls: ['./upload-file-multi-tab.component.scss'],
})
export class UploadFileMultiTabComponent {
    @Input() tabConfigs!: FileTabConfig<any>[];

    @Output() validationErrors: EventEmitter<Map<string, ZodError>> =
        new EventEmitter();
    @Output() tabResults: EventEmitter<Map<string, any[]>> = new EventEmitter();
    @Output() clearFiles: EventEmitter<boolean> = new EventEmitter();
    @Output() emitFiles: EventEmitter<NgxFileDropEntry[]> = new EventEmitter();
    @Output() emitFilesDetails: EventEmitter<
        Pick<UploadedFileDetail, 'name' | 'extension'>[]
    > = new EventEmitter();

    files: NgxFileDropEntry[] | undefined = undefined;
    fileName?: string[];

    constructor(
        private xlsxService: XlsxService,
        private readonly snackBarService: SnackBarService
    ) {}

    async getRowsFromFile(files: NgxFileDropEntry[]) {
        this.files = files;

        try {
            const tabData = await this.xlsxService.handleMultiTabUploadedFile(
                this.files,
                this.tabConfigs
            );

            this.fileName = this.xlsxService.getNamesFromFiles(this.files);
            this.emitFilesDetails.emit(this.getFilesDetails(this.fileName));

            const errors = new Map<string, ZodError>();
            const validatedResults = new Map<string, any[]>();

            for (const tabConfig of this.tabConfigs) {
                const rows = tabData.get(tabConfig.tabName);

                if (!rows) {
                    continue;
                }

                try {
                    const validated = tabConfig.schema.parse(rows);
                    validatedResults.set(tabConfig.tabName, validated);
                } catch (error) {
                    if (error instanceof ZodError) {
                        console.error(
                            `Zod's parse error for tab ${tabConfig.tabName}:`,
                            error
                        );
                        errors.set(tabConfig.tabName, error);
                    }
                }
            }

            if (errors.size > 0) {
                this.validationErrors.emit(errors);
                this.snackBarService.displayMessage(
                    `Erro de validação em ${errors.size} aba(s)!`,
                    MessageType.Error
                );
                return;
            }

            this.tabResults.emit(validatedResults);
            this.emitFiles.emit(this.files);
            this.snackBarService.displayMessage(
                'Sucesso ao importar todas as abas da planilha',
                MessageType.Success
            );
        } catch (error) {
            console.error('Error processing multi-tab file:', error);
            this.snackBarService.displayMessage(
                'Erro ao importar a planilha!',
                MessageType.Error
            );
        }
    }

    removeFiles() {
        this.files = undefined;
        this.fileName = undefined;
        this.clearFiles.emit(true);
    }

    getFilesDetails(
        files: string[]
    ): Pick<UploadedFileDetail, 'name' | 'extension'>[] {
        return files.map((file) => ({
            name: file,
            extension: file.split('.').reverse()[0],
        }));
    }
}
